import Promise from 'bluebird';
import {SubmissionError, getFormValues, change} from 'redux-form';
import qs from 'qs';
import flatten from 'lodash/flatten';
import groupBy from 'lodash/groupBy';
import forEach from 'lodash/forEach';
import find from 'lodash/find';
import get from 'lodash/get';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import uniqBy from 'lodash/uniqBy';
import some from 'lodash/some';
import keys from 'lodash/keys';

import http from '../../../../shared/http';
import endpoints from '../../../../../config/endpoints';
import { getTargetValues } from '../targeting';
import { actions as parentActions } from '../../../editor';

const defaultInitialState = {
	listOfActivePanIndex: [],
	currenciesPanIndexMap: {},
	nullifiedBoosts: [],
	field: {
		location_id: null,
		currency: null,
		amount: null,
		status: 'active'
	},
	isCommentFormVisible: false
};

const createIndividualTermsForm = ({ namespace, stateNodeName }) => {
	// action types
	const TERMS_FORM_SUBMIT_SUCCESS = `${namespace}/${stateNodeName}-submit-success`;
	const TERMS_FORM_SUBMIT_ERROR = `${namespace}/${stateNodeName}-submit-error`;

	const TERMS_DELETE_SUCCESS = `${namespace}/${stateNodeName}-delete-success`;
	const TERMS_DELETE_ERROR = `${namespace}/${stateNodeName}-delete-error`;

	const SHOW_TARGETS = `${namespace}/${stateNodeName}-show-targets`;
	const HIDE_TARGETS = `${namespace}/${stateNodeName}-hide-targets`;

	const CHANGE_CURRENCY = `${namespace}/${stateNodeName}-change-currency`;

	const COMMENT_IMAGE_UPDATE_SUCCESS = `${namespace}/${stateNodeName}-comment-image-update-success`;

	const COMMENT_FORM_STORE_IMAGE = `${namespace}/${stateNodeName}-comment-form-store-image`;

	const TOGGLE_COMMENT_FORM = `${namespace}/${stateNodeName}-toggle-comment-form`;

	const OFFER_ACCESS_SUCCESS = `${namespace}/${stateNodeName}-offer-access-success`;
	const OFFER_ACCESS_ERROR = `${namespace}/${stateNodeName}-offer-access-error`;

	const AFFILIATES_LOOKUP_SUCCESS = `${namespace}/${stateNodeName}-affiliates-lookup-success`;
	const AFFILIATES_LOOKUP_ERROR = `${namespace}/${stateNodeName}-affiliates-lookup-error`;

	const NULLIFY_BOOST = `${namespace}/${stateNodeName}-nullify-boost`;

	// initState
	const initialState = {
		...defaultInitialState
	};

	// actions
	const actions = {
		prepareTermsFormSubmit: formData => (dispatch, getState) => {
			return actions.termsFormSubmit(formData)(dispatch, getState);
		},
		submitWithComment: () => async (dispatch, getState)=> {
			const individualTermsForm = getFormValues('individualTermsForm')(getState());
			const reason = {
				payout_change_reason: individualTermsForm.payout_change_reason[0].value,
				payout_change_comment: individualTermsForm.payout_change_comment
			};
			const { reason_image } = getState().programsEditor.individualTermsForm;

			if (reason_image) {
				const result = await actions.sendCommentImage(reason_image)(dispatch, getState);
				if (result.type === COMMENT_IMAGE_UPDATE_SUCCESS) {
					reason.reason_image = result.payload.data.imageName;
				}
			}
			return actions.termsFormSubmit(individualTermsForm, reason)(dispatch, getState)
				.then(() => {
					dispatch({type: TOGGLE_COMMENT_FORM});
				});
		},
		toggleCommentForm: () => ({
			type: TOGGLE_COMMENT_FORM
		}),
		termsFormSubmit: (formData, reason = {}) => (dispatch, getState) => {
			const { individualTerms, individualTermsForm, offer } = getState().programsEditor;
			const nullifiedBoosts = individualTermsForm.nullifiedBoosts;
			const currencies = getState().root.lists['available-currencies'];

			const initValues = getInitialValueFromState(individualTerms);

			const dirtyFields = getDirtyFields(initValues, formData.terms, nullifiedBoosts);

			if (dirtyFields.length === 0) return;

			const masterTermsDecreaseWarning = some(
				dirtyFields,
				field => field.affiliate[0].is_master && field.hasLoweredPayouts
			)
				? confirm(
					'You are about to decrease individual terms for a master account, please confirm. Child payouts can change.'
				)
				: true;

			const savedAffiliates = keys(groupBy(individualTerms, 'affiliate_id'));

			const masterAddedOfferWarning = some(
				dirtyFields,
				field => !savedAffiliates.includes(`${field.affiliate[0].value}`) && field.affiliate[0].is_master
			)
				? confirm(
					'You are about to add individual terms to a master account, please confirm. Child payouts can change.'
				)
				: true;

			if (!masterTermsDecreaseWarning || !masterAddedOfferWarning) {
				return;
			}

			// if submit bug please remove dirtyFields to formData.terms
			const prepareData = dirtyFields.map(entity => {
				const fields = entity.targets.map(field => {
					const correctFields = currencies.filter(currency => !!field[currency.id]).map(currency => ({
						...individualTermsForm.field,
						...field,
						status: 'active',
						location_id: field['location_id'] && field['location_id'][0] ? field['location_id'][0]['value'] : '',
						currency: currency.id,
						amount: field[currency.id]
					}));

					return correctFields.length
						? correctFields
						: {
							...individualTermsForm.field
						};
				});

				return {
					affiliateId: (entity.affiliate[0] && entity.affiliate[0].value) || null,
					fields: flatten(fields).filter(entity => !!entity.currency),
					isMaster: entity.affiliate[0] && entity.affiliate[0].is_master
				};
			});
			const getRequest = formData => {
				const urls = formData.map(
					entity => `${endpoints.OFFERS.INDIVIDUAL_TERMS.replace('{id}', offer.id)}/${entity.affiliateId}`
				);
				const data = formData.map(entity => entity.fields);

				return urls.length
					? http({
						url: urls,
						method: 'put',
						actions: [TERMS_FORM_SUBMIT_SUCCESS, TERMS_FORM_SUBMIT_ERROR],
						data: data.map(
							item => item.map(term => ({
								...term,
								boost_till: processBoostUpdate(term, nullifiedBoosts),
								...reason
							})))
					}) : () => Promise.resolve({ type: TERMS_FORM_SUBMIT_SUCCESS });
			};

			const withoutDuplicateAffs = uniqBy(prepareData, item => item.affiliateId);

			const request = getRequest(withoutDuplicateAffs.filter(i => !i.isMaster));
			const masterRequest = getRequest(withoutDuplicateAffs.filter(i => i.isMaster));

			dispatch(parentActions.toggleProgressBar());

			return dispatch(actions.offerAccess(offer.id, withoutDuplicateAffs.map(i => i.affiliateId))).then(action => {
				if (action.type === OFFER_ACCESS_SUCCESS) {
					return masterRequest(dispatch).then(action => {
						if (action.type === TERMS_FORM_SUBMIT_ERROR && action.payload.response.status === 400) {
							return handleValidation(action, formData);
						}

						return request(dispatch).then(action => {
							if (action.type === TERMS_FORM_SUBMIT_SUCCESS) {
								dispatch(parentActions.fetchIndividualTerms(offer.id));
							}

							dispatch(parentActions.toggleProgressBar());

							if (action.type === TERMS_FORM_SUBMIT_ERROR && action.payload.response.status === 400) {
								return handleValidation(action, formData);
							}
						});
					});
				}
			});
		},

		individualTermsDeleteRequest: (affiliateId = null, locationId = null, isResetToDefault = false) => (
			dispatch,
			getState
		) => {
			const { individualTerms, offer } = getState().programsEditor;
			const criteria = Object.assign({ affiliate_id: affiliateId }, locationId ? { location_id: locationId } : {});

			const grouped = groupBy(uniqBy(filter(individualTerms, criteria), 'location_id'), entity => entity.affiliate_id);

			if (isEmpty(grouped)) return Promise.resolve(dispatch({ type: TERMS_DELETE_SUCCESS }));

			const query = qs.stringify(
				{
					location_ids: grouped[affiliateId].map(entity => entity.location_id),
					is_reset_to_default: isResetToDefault
				},
				{ arrayFormat: 'brackets' }
			);

			const request = http({
				url: `${endpoints.OFFERS.INDIVIDUAL_TERMS.replace('{id}', offer.id)}/${affiliateId}?${query}`,
				method: 'delete',
				actions: [TERMS_DELETE_SUCCESS, TERMS_DELETE_ERROR]
			});

			return request(dispatch);
		},

		individualTermsDelete: (affiliateId = null, locationId = null) => (dispatch) => {
			return dispatch(actions.individualTermsDeleteRequest(affiliateId, locationId)).then(action => {
				if (action.type === TERMS_DELETE_SUCCESS) {
					return true;
				}
			});
		},
		individualTermsDeleteAndFetch: (affiliateId = null, locationId = null) => (dispatch, getState) => {
			const { offer } = getState().programsEditor;
			dispatch(parentActions.toggleProgressBar());
			dispatch(actions.individualTermsDeleteRequest(affiliateId, locationId)).then(action => {
				if (action.type === TERMS_DELETE_SUCCESS) {
					dispatch(parentActions.fetchIndividualTerms(offer.id));
				}
			});
		},

		termsResetToDefault: (affiliateId, locationId) => (dispatch, getState) => {
			const { targeting, offer: { id } } = getState().programsEditor;
			const defaultTerms = targeting.filter(entity => entity['location_id'] === locationId);
			const request = (offerId, affiliateId) => {
				const request = http({
					url: `${endpoints.OFFERS.INDIVIDUAL_TERMS.replace('{id}', offerId)}/${affiliateId}`,
					method: 'put',
					actions: [TERMS_FORM_SUBMIT_SUCCESS, TERMS_FORM_SUBMIT_ERROR],
					data: defaultTerms
				});

				dispatch(parentActions.toggleProgressBar());

				request(dispatch).then(action => {
					dispatch(parentActions.toggleProgressBar());

					if (action.type === TERMS_FORM_SUBMIT_SUCCESS) {
						dispatch(parentActions.fetchIndividualTerms(offerId));
					}

					if (action.type === TERMS_FORM_SUBMIT_ERROR) {
						let isChildAffiliateError =
							action.payload.response.data &&
							some(action.payload.response.data, e => e.message === `can't setup these payouts for child account`);

						if (isChildAffiliateError) {
							return alert(`Child payouts should not be higher than masters`);
						}
					}
				});
			};

			if (defaultTerms.length === 1 && !defaultTerms[0].amount) {
				return dispatch(actions.individualTermsDelete(affiliateId, locationId));
			}

			if (defaultTerms.length === 1 && defaultTerms[0].amount) {
				let isResetToDefault = true;
				return dispatch(actions.individualTermsDeleteRequest(affiliateId, locationId, isResetToDefault)).then(
					action => {
						request(id, affiliateId);
					}
				);
			}

			request(id, affiliateId);
		},

		termsShowTargets: index => ({
			type: SHOW_TARGETS,
			payload: index
		}),

		termsHideTargets: index => ({
			type: HIDE_TARGETS,
			payload: index
		}),

		changeCurrency: (index, currency) => ({
			type: CHANGE_CURRENCY,
			payload: {
				index,
				currency
			}
		}),

		offerAccess: (offerId, affiliateIds) => (dispatch, getState) => {
			if (!offerId || !affiliateIds) return Promise.resolve();
			const urls = affiliateIds.map(id => endpoints.OFFERS.GRAND_ACCESS.replace('{id}', offerId));

			const request = http({
				url: urls,
				method: 'put',
				actions: [OFFER_ACCESS_SUCCESS, OFFER_ACCESS_ERROR],
				data: affiliateIds.map(id => ({ affiliate_id: id }))
			});

			return request(dispatch);
		},

		nullifyBoostOnSubmit: target => (dispatch, getState) => {
			const {
				isBoosted, payoutPath, defaultCurrency, locationId
			} = target;

			if (isBoosted) {
				const offerDefaultTargets = get(getState(), 'programsEditor.targeting', []);
				const defaultTarget = find(offerDefaultTargets, {location_id: locationId, currency: defaultCurrency});
				dispatch(change('individualTermsForm', `${payoutPath}.${defaultCurrency}`, defaultTarget.amount));
			}

			dispatch({
				type: NULLIFY_BOOST,
				payload: target
			});
		},

		indTermsAffiliatesLookup: (affiliateIdsInForm, text) => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.LOOKUPS.AFFILIATES}?query=${text}`,
				method: 'get',
				actions: [AFFILIATES_LOOKUP_SUCCESS, AFFILIATES_LOOKUP_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === AFFILIATES_LOOKUP_SUCCESS) {
					const affiliatesNotAlreadyInForm = action.payload.data.filter(aff => {
						return affiliateIdsInForm.indexOf(aff.id) === -1;
					});
					return {
						options: affiliatesNotAlreadyInForm.map(a => ({ ...a, value: a.id, label: a.text }))
					};
				}
			});
		}
	};

	// reducers
	const reducers = {
		[SHOW_TARGETS]: (state, action) => ({
			...state,
			individualTermsForm: {
				...state.individualTermsForm,
				listOfActivePanIndex: [].concat(state.individualTermsForm.listOfActivePanIndex, action.payload)
			}
		}),
		[HIDE_TARGETS]: (state, action) => ({
			...state,
			individualTermsForm: {
				...state.individualTermsForm,
				listOfActivePanIndex: state.individualTermsForm.listOfActivePanIndex.filter(index => index !== action.payload)
			}
		}),
		[TERMS_DELETE_SUCCESS]: (state, action) => ({
			...state,
			individualTermsForm: { ...initialState }
		}),
		[TERMS_FORM_SUBMIT_SUCCESS]: (state, action) => ({
			...state,
			individualTermsForm: {
				...state.individualTermsForm,
				nullifiedBoosts: []
			}
		}),
		[CHANGE_CURRENCY]: (state, action) => ({
			...state,
			individualTermsForm: {
				...state.individualTermsForm,
				currenciesPanIndexMap: {
					...state.individualTermsForm.currenciesPanIndexMap,
					[action.payload.index]: action.payload.currency
				}
			}
		}),
		[COMMENT_FORM_STORE_IMAGE]: (state, action) => ({
			...state,
			individualTermsForm: {
				...state.individualTermsForm,
				reason_image: action.payload
			}
		}),
		[TOGGLE_COMMENT_FORM]: (state, action) => ({
			...state,
			individualTermsForm: {
				...state.individualTermsForm,
				isCommentFormVisible: !state.individualTermsForm.isCommentFormVisible
			}
		}),

		[NULLIFY_BOOST]: (state, action) => ({
			...state,
			individualTermsForm: {
				...state.individualTermsForm,
				// concat if not already in state
				nullifiedBoosts: find(state.individualTermsForm.nullifiedBoosts, {
					offerId: action.payload.offerId,
					affiliateId: action.payload.affiliateId,
					locationId: action.payload.locationId
				})
					? state.individualTermsForm.nullifiedBoosts
					: state.individualTermsForm.nullifiedBoosts.concat(action.payload)
			}
		})
	};

	// selectors
	const selectors = {
		getIndividualTermsFormData: state => {
			const {
				targeting,
				individualTerms,
				individualTermsForm: { listOfActivePanIndex, currenciesPanIndexMap, nullifiedBoosts }
			} = state.programsEditor;
			const availableCurrencies = state.root.lists['available-currencies'];
			const offerTargeting = getTargetValues(targeting);
			const initialValues = {
				terms: getInitialValueFromState(individualTerms)
			};

			return {
				initialValues,
				offerTargeting,
				listOfActivePanIndex,
				availableCurrencies,
				currenciesPanIndexMap,
				nullifiedBoosts
			};
		}
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

// functions
const getInitialValueFromState = (termsData) => {
	const res = [];
	const groupedByAffiliate = groupBy(termsData, entity => entity.affiliate_id);

	forEach(groupedByAffiliate, (entity, affiliateId) => {
		res.push({
			affiliate: [
				{
					value: +affiliateId,
					label: get(find(entity, { affiliate_id: +affiliateId }), 'affiliate'),
					is_master: get(find(entity, { affiliate_id: +affiliateId }), 'is_master')
				}
			],
			targets: getTargetValues(entity)
		});
	});

	return res;
};

// return value is different from the initialized value for submit
const getDirtyFields = (initialValues, formValues, nullifiedBoosts) => {
	const res = [];

	formValues.forEach(formValueEntity => {
		if (isEmpty(initialValues)) {
			res.push(formValueEntity);
		} else if (isNonInInitialValues(formValueEntity, initialValues)) {
			res.push(formValueEntity);
		} else {
			initialValues.forEach(initialValueEntity => {
				if (
					formValueEntity.affiliate[0] &&
					formValueEntity.affiliate[0].value === initialValueEntity.affiliate[0].value
				) {
					const changedTargets = formValueEntity.targets
						.map(newTarget => {
							const oldTarget = find(initialValueEntity.targets, {
								location: newTarget.location,
								offer_id: newTarget.offer_id,
								affiliate_id: newTarget.affiliate_id
							});

							// this PR will help with the history of changes: https://github.com/Everad/cpa2/pull/2434

							if (!oldTarget) return true;
							return Number(newTarget.rub || 0) !== Number(oldTarget.rub || 0)
								|| Number(newTarget.usd || 0) !== Number(oldTarget.usd || 0)
								|| Number(newTarget.cap || 0) !== Number(oldTarget.cap || 0)
								|| oldTarget.is_locked !== newTarget.is_locked;
						})
						.filter(Boolean);

					if (changedTargets.length) {
						res.push(formValueEntity);
					}
				}
			});
		}
	});

	return res;
};

// functions
const isNonInInitialValues = (formValueEntity, initialValues) => {
	return isEmpty(
		initialValues.filter(
			initialValueEntity =>
				formValueEntity.affiliate[0] && formValueEntity.affiliate[0].value === initialValueEntity.affiliate[0].value
		)
	);
};

const handleValidation = (action, formData) => {
	// child payouts should not be higher than masters:
	// tmp solution until submission error mapping gets improved
	let isChildAffiliateError =
		action.payload.response.data &&
		some(action.payload.response.data, e => e.message === `can't setup these payouts for child account`);

	if (isChildAffiliateError) {
		return alert(`Child payouts should not be higher than masters`);
	}

	const validationsErrors = [];

	formData['terms'].forEach((field, index) => {
		validationsErrors[index] = {};

		if (!field.affiliate || (Array.isArray(field.affiliate) && !field.affiliate.length)) {
			validationsErrors[index]['affiliate'] = 'Affiliate should not be empty';
		}

		const validationsTargetsError = [];

		field['targets'].forEach((targetsField, targetsIndex) => {
			validationsTargetsError[targetsIndex] = {};
			if (!targetsField.rub && !targetsField.usd) {
				validationsTargetsError[targetsIndex]['rub'] = 'rub or usd should be >= 0.01';
				validationsTargetsError[targetsIndex]['usd'] = 'usd or rub should be >= 0.01';
			} else {
				if (targetsField.rub && isNaN(parseFloat(targetsField.rub))) {
					validationsTargetsError[targetsIndex]['rub'] = 'rub must be number';
				}
				if (targetsField.usd && isNaN(parseFloat(targetsField.usd))) {
					validationsTargetsError[targetsIndex]['usd'] = 'usd must be number';
				}
			}
		});

		validationsErrors[index]['targets'] = validationsTargetsError;
	});

	throw new SubmissionError({
		terms: validationsErrors
	});
};

function processBoostUpdate(payout, nullifiedBoosts = []) {
	const { offer_id, location_id, affiliate_id, boost_till } = payout;
	if (find(nullifiedBoosts, {offerId: offer_id, locationId: location_id, affiliateId: affiliate_id})) {
		return null;
	}
	return boost_till;
}

export default createIndividualTermsForm;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/programs/components/forms/individual-terms/index.js